import { useEffect, useState } from "react";
import { X, Cloud, Wind, Droplets, Thermometer, Sun, CloudRain, Loader2 } from "lucide-react";
import { authFetch } from "@/lib/api";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function WeatherModal({ lat, lon, parcelName, onClose }) {
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const res = await authFetch(`${API}/agro/weather?lat=${lat}&lon=${lon}`);
      if (res.ok) setWeather(await res.json());
      setLoading(false);
    })();
  }, [lat, lon]);

  return (
    <div className="fixed inset-0 z-[2000] bg-black/50 flex items-end sm:items-center justify-center p-2 sm:p-4" data-testid="weather-modal">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col shadow-2xl">
        <div className="flex items-center justify-between px-4 py-3 border-b bg-gradient-to-r from-cyan-600 to-cyan-700 text-white">
          <div>
            <div className="text-[10px] uppercase tracking-widest opacity-90">Météo locale</div>
            <div className="text-sm font-bold truncate">{parcelName || "Ma parcelle"}</div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-white/20" data-testid="close-weather-btn">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4" style={{ WebkitOverflowScrolling: "touch" }}>
          {loading ? (
            <div className="flex items-center justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-cyan-600" /></div>
          ) : !weather ? (
            <p className="text-sm text-gray-500 text-center py-6">Météo indisponible</p>
          ) : (
            <>
              {/* Current */}
              <div className="text-center py-3 mb-3">
                <div className="text-5xl font-bold" style={{ color: "#0891B2" }}>{Math.round(weather.current?.temperature ?? 0)}°C</div>
                <div className="text-xs text-gray-500 uppercase mt-1">Maintenant</div>
              </div>
              <div className="grid grid-cols-3 gap-2 mb-4">
                <div className="p-2 rounded-lg bg-cyan-50 text-center">
                  <Wind className="w-4 h-4 mx-auto text-cyan-700 mb-1" />
                  <div className="text-[10px] uppercase text-gray-500">Vent</div>
                  <div className="text-sm font-bold">{Math.round(weather.current?.wind_speed ?? 0)} km/h</div>
                </div>
                <div className="p-2 rounded-lg bg-cyan-50 text-center">
                  <Droplets className="w-4 h-4 mx-auto text-cyan-700 mb-1" />
                  <div className="text-[10px] uppercase text-gray-500">Humidité</div>
                  <div className="text-sm font-bold">{Math.round(weather.current?.humidity ?? 0)}%</div>
                </div>
                <div className="p-2 rounded-lg bg-cyan-50 text-center">
                  <CloudRain className="w-4 h-4 mx-auto text-cyan-700 mb-1" />
                  <div className="text-[10px] uppercase text-gray-500">Pluie 24h</div>
                  <div className="text-sm font-bold">{(weather.current?.precipitation ?? 0).toFixed(1)} mm</div>
                </div>
              </div>
              {/* 7-day forecast */}
              <h4 className="text-xs uppercase font-bold text-gray-500 mb-2">Prévisions 7 jours</h4>
              <div className="space-y-1" data-testid="weather-forecast-list">
                {(weather.forecast || weather.daily_forecast || []).slice(0, 7).map((d, i) => (
                  <div key={i} className="flex items-center gap-3 p-2 rounded-lg bg-gray-50">
                    <div className="text-xs font-semibold text-gray-700 w-16">{new Date(d.date).toLocaleDateString("fr-FR", { weekday: "short", day: "numeric" })}</div>
                    <div className="flex-1 flex items-center gap-2">
                      <Thermometer className="w-3 h-3 text-orange-500" />
                      <span className="text-xs">{Math.round(d.temp_min)}° / <b>{Math.round(d.temp_max)}°</b></span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Sun className="w-3 h-3 text-yellow-500" />
                      <span className="text-[10px] text-gray-600">{Math.round((d.sunshine_hours || 0))}h</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <CloudRain className="w-3 h-3 text-blue-500" />
                      <span className="text-[10px] text-gray-600">{((d.precipitation_mm ?? d.precipitation) || 0).toFixed(1)} mm</span>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-[10px] text-gray-400 text-center mt-3">Source : Open-Meteo</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
